/**
 * SEO Metadata Configuration
 * Centralized metadata for all pages to ensure consistency
 */

export interface PageMetadata {
  title: string;
  description: string;
  keywords?: string;
}

export const SEO_METADATA = {
  // Homepage
  home: {
    title: "Medical Tourism India | Save 70% | TrustedHeal",
    description: "Discover world-class medical treatments in India. JCI-accredited hospitals, expert doctors, and transparent pricing. Save up to 70% on healthcare.",
    keywords: "medical tourism India, affordable healthcare India, dental tourism, JCI hospitals India"
  },

  // Main Pages
  hospitals: {
    title: "Top Hospitals in India | JCI Accredited | TrustedHeal",
    description: "Browse JCI & NABH accredited hospitals in India. World-class facilities, expert medical teams, and international patient care.",
    keywords: "hospitals India, JCI accredited hospitals, NABH hospitals, medical facilities India"
  },

  doctors: {
    title: "Expert Doctors India | Medical Specialists | TrustedHeal",
    description: "Find top JCI/NABH accredited doctors and specialists in India. Expert surgeons for dental, cardiac, orthopedic, and cosmetic procedures.",
    keywords: "doctors India, medical specialists, surgeons India, healthcare professionals"
  },

  contact: {
    title: "Contact Us | Free Medical Quote | TrustedHeal",
    description: "Get a free medical tourism quote. Speak to our experts about treatment in India, visa assistance, and travel planning.",
    keywords: "medical tourism quote, contact TrustedHeal, healthcare consultation"
  },

  blog: {
    title: "Medical Tourism Blog | Healthcare Insights | TrustedHeal",
    description: "Expert insights on medical tourism, treatment guides, cost comparisons, and patient success stories from India.",
    keywords: "medical tourism blog, healthcare articles, treatment guides, patient stories"
  },

  treatments: {
    title: "Medical Treatments in India | Cost & Quality | TrustedHeal",
    description: "Explore affordable medical treatments in India. Compare costs, find specialists, and book consultations for world-class healthcare.",
    keywords: "medical treatments India, healthcare procedures, treatment costs India"
  },

  compareCosts: {
    title: "Compare Medical Costs | India vs USA | TrustedHeal",
    description: "Compare medical treatment costs between India and USA. See how much you can save on quality healthcare procedures.",
    keywords: "medical cost comparison, India vs USA healthcare costs, treatment pricing"
  },

  privacyPolicy: {
    title: "Privacy Policy | Data Protection | TrustedHeal",
    description: "Learn how TrustedHeal protects your personal information and medical data. Our commitment to privacy and security.",
    keywords: "privacy policy, data protection, medical data security"
  },

  termsOfService: {
    title: "Terms of Service | User Agreement | TrustedHeal",
    description: "Read TrustedHeal's terms of service and user agreement. Understand your rights and responsibilities.",
    keywords: "terms of service, user agreement, legal terms"
  },

  medicalTourism: {
    title: "Medical Tourism India Guide | Complete Info | TrustedHeal",
    description: "Complete guide to medical tourism in India. Learn about procedures, costs, hospitals, visa requirements, and travel planning.",
    keywords: "medical tourism guide, India healthcare guide, medical travel information"
  },

  // Treatment Categories
  treatmentCategories: {
    dental: {
      title: "Dental Treatments India | Save 85% | TrustedHeal",
      description: "Affordable dental treatments in India. Implants, veneers, crowns, and smile makeovers at JCI-accredited clinics.",
      keywords: "dental tourism India, dental implants India, teeth treatment India"
    },
    cardiac: {
      title: "Cardiac Surgery India | Heart Treatment | TrustedHeal",
      description: "World-class cardiac surgery in India. CABG, angioplasty, valve replacement by expert cardiologists.",
      keywords: "cardiac surgery India, heart treatment India, cardiology India"
    },
    orthopedics: {
      title: "Orthopedic Surgery India | Joint Replacement | TrustedHeal",
      description: "Advanced orthopedic surgery in India. Knee, hip, shoulder replacement and spine surgery.",
      keywords: "orthopedic surgery India, joint replacement India, knee replacement India"
    },
    oncology: {
      title: "Cancer Treatment India | Oncology Care | TrustedHeal",
      description: "Comprehensive cancer treatment in India. Chemotherapy, radiation, surgery by expert oncologists.",
      keywords: "cancer treatment India, oncology India, cancer care India"
    },
    cosmetic: {
      title: "Cosmetic Surgery India | Aesthetic Procedures | TrustedHeal",
      description: "Affordable cosmetic surgery in India. Rhinoplasty, liposuction, breast augmentation by expert surgeons.",
      keywords: "cosmetic surgery India, plastic surgery India, aesthetic procedures India"
    },
    fertility: {
      title: "IVF Treatment India | Fertility Care | TrustedHeal",
      description: "Advanced IVF and fertility treatments in India. High success rates at affordable costs.",
      keywords: "IVF India, fertility treatment India, infertility care India"
    },
    ophthalmology: {
      title: "Eye Surgery India | LASIK & Cataract | TrustedHeal",
      description: "Advanced eye surgery in India. LASIK, cataract, retina surgery by expert ophthalmologists.",
      keywords: "eye surgery India, LASIK India, cataract surgery India"
    },
    wellness: {
      title: "Wellness & Ayurveda India | Holistic Care | TrustedHeal",
      description: "Authentic Ayurveda and wellness treatments in India. Panchakarma, yoga retreats, and holistic healing.",
      keywords: "Ayurveda India, wellness retreats India, holistic healing India"
    },
    rehabilitation: {
      title: "Rehabilitation Services India | Recovery Care | TrustedHeal",
      description: "Comprehensive rehabilitation services in India. Post-surgery recovery, physiotherapy, and pain management.",
      keywords: "rehabilitation India, physiotherapy India, recovery care India"
    },
    mental_health: {
      title: "Mental Health Care India | Therapy & Counseling | TrustedHeal",
      description: "Professional mental health services in India. Therapy, counseling, and psychiatric care.",
      keywords: "mental health India, therapy India, counseling India"
    }
  },

  // Blog Categories
  blogCategories: {
    dental: {
      title: "Dental Tourism Articles | Expert Insights | TrustedHeal",
      description: "Expert articles on dental tourism, implants, veneers, and smile makeovers in India.",
      keywords: "dental tourism articles, dental implants guide, teeth treatment blog"
    },
    cardiac: {
      title: "Cardiac Care Articles | Heart Health | TrustedHeal",
      description: "Expert insights on cardiac surgery, heart health, and cardiovascular treatments in India.",
      keywords: "cardiac care articles, heart surgery blog, cardiology insights"
    },
    orthopedics: {
      title: "Orthopedic Articles | Joint Care | TrustedHeal",
      description: "Expert articles on joint replacement, spine surgery, and orthopedic care in India.",
      keywords: "orthopedic articles, joint replacement blog, bone health"
    },
    oncology: {
      title: "Cancer Care Articles | Oncology Insights | TrustedHeal",
      description: "Expert insights on cancer treatment, therapies, and oncology care in India.",
      keywords: "cancer care articles, oncology blog, cancer treatment insights"
    },
    cosmetic: {
      title: "Cosmetic Surgery Articles | Beauty Insights | TrustedHeal",
      description: "Expert articles on cosmetic surgery, aesthetic procedures, and beauty treatments in India.",
      keywords: "cosmetic surgery articles, plastic surgery blog, beauty procedures"
    },
    fertility: {
      title: "Fertility Articles | IVF Insights | TrustedHeal",
      description: "Expert insights on IVF, fertility treatments, and reproductive health in India.",
      keywords: "fertility articles, IVF blog, reproductive health insights"
    },
    wellness: {
      title: "Wellness Articles | Holistic Health | TrustedHeal",
      description: "Expert articles on Ayurveda, wellness retreats, and holistic healing in India.",
      keywords: "wellness articles, Ayurveda blog, holistic health insights"
    },
    "medical-tourism": {
      title: "Medical Tourism Articles | Travel Insights | TrustedHeal",
      description: "Expert insights on medical tourism, travel planning, and healthcare abroad.",
      keywords: "medical tourism articles, healthcare travel blog, medical travel insights"
    },
    "preventive-care": {
      title: "Preventive Care Articles | Health Screening | TrustedHeal",
      description: "Expert articles on preventive care, health screenings, and wellness checkups in India.",
      keywords: "preventive care articles, health screening blog, wellness checkup insights"
    }
  }
} as const;

/**
 * Generate page title with proper length validation
 */
export function generatePageTitle(title: string, suffix: string = "TrustedHeal"): string {
  const fullTitle = `${title} | ${suffix}`;
  
  // Truncate if too long (max 60 chars)
  if (fullTitle.length > 60) {
    const maxTitleLength = 60 - suffix.length - 3; // 3 for " | "
    return `${title.substring(0, maxTitleLength)} | ${suffix}`;
  }
  
  return fullTitle;
}

/**
 * Validate and truncate description
 */
export function validateDescription(description: string): string {
  if (description.length < 70) {
    console.warn(`Description too short: ${description.length} chars (min 70)`);
  }
  
  if (description.length > 155) {
    return description.substring(0, 155);
  }
  
  return description;
}
